import Paper from "@components/Paper/Paper";
import PaperItem from "@components/Paper/PaperItem";
import {
  isHSEWasteDisposalSlops,
  isHSEWasteDisposalSludge,
} from "@features/vessel-call/vessel-call-status/PortServices/flow/hse/utils";
import {
  isWasteDisposalGarbage,
  isWasteDisposalSlops,
} from "@features/vessel-call/vessel-call-status/PortServices/flow/utils";
import { isShipToShipService } from "@features/vessel-call/vessel-call-status/PortServices/form/utils";
import BunkeringFields, {
  isBunkeringPortService,
} from "@features/vessel-call/vessel-call-status/PortServices/preview/BunkeringFields";
import DebunkeringFields, {
  isDebunkeringPortService,
} from "@features/vessel-call/vessel-call-status/PortServices/preview/DebunkeringFields";
import SludgeFields from "@features/vessel-call/vessel-call-status/PortServices/preview/SludgeFields";
import { getPortServiceCategoryLabel } from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import WasteDisposalGarbage from "@features/vessel-call/vessel-call-status/PortServices/preview/WasteDisposalGarbage";
import WasteDisposalSlopsFields from "@features/vessel-call/vessel-call-status/PortServices/preview/WasteDisposalSlopsFields";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { PortService } from "@services/port-services/types";
import { paths } from "@utils/navigation";
import { format } from "date-fns";
import { t } from "i18next";
import { useNavigate, useParams } from "react-router-dom";
import { isShipChandlingLifeboatsService } from "../form/utils";
import ShipToShipFields from "./ShipToShipFields";

const TEST_ID_BASE = "port-services-preview";
const wrapperStyleClass = "border-b border-[#F1F2F4] pb-3";
const wrapperLastStyleClass = "pb-0";
const transStyleClass = "font-normal text-[#646A77]";

const formatShipChandlingDirection = (langBase: string, direction?: string) => {
  if (!direction) {
    return "-";
  }

  const directionNormalized = direction.toLowerCase().replace("_", "-");
  const defaultDirection = direction
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

  return t(`${langBase}.ship-chandling-directions.${directionNormalized}`, {
    defaultValue: defaultDirection,
  });
};

const getShipChandlingResources = (portServiceDetails: PortService) =>
  portServiceDetails.resourcesAndEquipment?.length
    ? portServiceDetails.resourcesAndEquipment.map((resource) => ({
        name: resource.resourceTypeName || "-",
        count: resource.numberOfResources || "-",
        start: resource.usageStartTime
          ? format(new Date(resource.usageStartTime), "dd/MM/yyyy' 'HH:mm")
          : null,
        end: resource.usageEndTime
          ? format(new Date(resource.usageEndTime), "dd/MM/yyyy' 'HH:mm")
          : null,
      }))
    : (portServiceDetails.additionalResources ?? []).map((resource) => ({
        name: resource.resourceTypeLabel || resource.resourceType || "-",
        count: resource.numberOfResources || "-",
        start: resource.usageStartTime
          ? format(new Date(resource.usageStartTime), "dd/MM/yyyy' 'HH:mm")
          : null,
        end: resource.usageEndTime
          ? format(new Date(resource.usageEndTime), "dd/MM/yyyy' 'HH:mm")
          : null,
      }));

interface ServiceDetailsProps {
  langBase: string;
  portServiceDetails: PortService;
}

const ServiceDetails: React.FC<ServiceDetailsProps> = ({ langBase, portServiceDetails }) => {
  const navigate = useNavigate();
  const isMarineDepartmentUser = useAuthStore((state) => state.isMarineDepartmentUser);
  const { id: currentVesselCallId, subModuleId } = useParams<{
    id?: string;
    subModuleId?: string;
  }>();
  const secondaryLinkedVessel = portServiceDetails.linkedVesselCalls?.find(
    (linkedVesselCall) => linkedVesselCall.role === "SECONDARY",
  );
  const otherLinkedVessel = currentVesselCallId
    ? portServiceDetails.linkedVesselCalls?.find(
        (linkedVesselCall) => linkedVesselCall.vesselCallId !== currentVesselCallId,
      )
    : secondaryLinkedVessel;
  const otherVesselImo = otherLinkedVessel?.imo || "-";
  const handleOtherVesselClick = () => {
    if (!otherLinkedVessel?.vesselCallId) return;

    const nextPath = subModuleId
      ? `/${paths.vesselCall.index}/${paths.vesselCall.vesselCallPreview}/${otherLinkedVessel.vesselCallId}/${paths.vesselCall.portServices}/${subModuleId}`
      : `/${paths.vesselCall.index}/${paths.vesselCall.vesselCallPreview}/${otherLinkedVessel.vesselCallId}/${paths.vesselCall.portServices}`;

    navigate(nextPath);
  };

  const isSludgeService = isHSEWasteDisposalSludge(
    portServiceDetails.serviceCategory,
    portServiceDetails.serviceType ?? "",
    portServiceDetails.serviceSubType ?? "",
  );

  const isWasteDisposalSlopsService = isWasteDisposalSlops(
    portServiceDetails.serviceCategory,
    portServiceDetails.serviceType ?? "",
  );
  const isHSEWasteDisposalSlopsService = isHSEWasteDisposalSlops(
    portServiceDetails.serviceCategory,
    portServiceDetails.serviceType ?? "",
    portServiceDetails.serviceSubType ?? "",
  );
  const isSlopsService = isWasteDisposalSlopsService || isHSEWasteDisposalSlopsService;

  const isWasteDisposalGarbageService = isWasteDisposalGarbage(
    portServiceDetails.serviceCategory,
    portServiceDetails.serviceType ?? "",
  );

  const isBunkeringService = isBunkeringPortService(portServiceDetails.serviceCategory);
  const isDebunkeringService = isDebunkeringPortService(portServiceDetails.serviceCategory);
  const isBunkerBargeService = isBunkeringService || isDebunkeringService;
  const isShipToShip = isShipToShipService(
    portServiceDetails.serviceCategory,
    portServiceDetails.serviceType,
    portServiceDetails.serviceSubType,
  );
  const isShipChandlingLifeboats = isShipChandlingLifeboatsService(
    portServiceDetails.serviceCategory,
    portServiceDetails.serviceType ?? "",
  );

  const hasCategoryAndType =
    !!(portServiceDetails.serviceCategory && portServiceDetails.serviceType) &&
    !portServiceDetails.serviceSubType;

  const lastWrapperStyle = hasCategoryAndType ? wrapperLastStyleClass : wrapperStyleClass;
  const shipToShipTopRowWrapperStyle = isShipToShip ? wrapperStyleClass : lastWrapperStyle;
  const canNavigateToOtherVessel =
    isMarineDepartmentUser && !!otherLinkedVessel?.vesselCallId && otherVesselImo !== "-";
  const shipChandlingResources = getShipChandlingResources(portServiceDetails);
  const serviceCategoryLabel = getPortServiceCategoryLabel(portServiceDetails.serviceCategory);

  return (
    <>
      {isWasteDisposalGarbageService ? (
        <WasteDisposalGarbage langBase={langBase} portServiceDetails={portServiceDetails} />
      ) : isShipChandlingLifeboats ? (
        <Paper variant="outlined">
          <p className="font-bold">{t(`${langBase}.service-details`)}</p>
          <div className="grid grid-cols-2 gap-x-8">
            <div>
              <PaperItem
                testId={`${TEST_ID_BASE}-service-category`}
                translate={t(`${langBase}.category`)}
                textStyles="font-medium"
                tranStyle={transStyleClass}
                wrapperStyle={wrapperStyleClass}
                labelClassName="w-full"
                valueClassName="w-full"
                text={serviceCategoryLabel || "-"}
              />
              <PaperItem
                testId={`${TEST_ID_BASE}-service-type`}
                translate={t(`${langBase}.type`)}
                textStyles="font-medium"
                tranStyle={transStyleClass}
                wrapperStyle={wrapperStyleClass}
                labelClassName="w-full"
                valueClassName="w-full"
                text={portServiceDetails.serviceType || "-"}
              />
              <PaperItem
                testId={`${TEST_ID_BASE}-service-subtype`}
                translate={t(`${langBase}.sub-type`)}
                textStyles="font-medium"
                tranStyle={transStyleClass}
                wrapperStyle={wrapperStyleClass}
                labelClassName="w-full"
                valueClassName="w-full"
                text={portServiceDetails.serviceSubType || "-"}
              />
              <PaperItem
                testId={`${TEST_ID_BASE}-ship-chandling-direction`}
                translate={t(`${langBase}.ship-chandling-direction`)}
                textStyles="font-medium"
                tranStyle={transStyleClass}
                wrapperStyle={wrapperLastStyleClass}
                labelClassName="w-full"
                valueClassName="w-full"
                text={formatShipChandlingDirection(
                  langBase,
                  portServiceDetails.shipChandlingDirection || portServiceDetails.direction,
                )}
              />
            </div>
            <div>
              <PaperItem
                testId={`${TEST_ID_BASE}-number-of-packages-handled`}
                translate={t(`${langBase}.number-of-packages-handled`)}
                textStyles="font-medium"
                tranStyle={transStyleClass}
                wrapperStyle={wrapperStyleClass}
                labelClassName="w-full"
                valueClassName="w-full"
                text={portServiceDetails.numberOfPackagesHandled || "-"}
              />
              <PaperItem
                testId={`${TEST_ID_BASE}-receiving-vessel-imo`}
                translate={t(`${langBase}.receiving-vessel-imo-number`)}
                textStyles="font-medium"
                tranStyle={transStyleClass}
                wrapperStyle={wrapperStyleClass}
                labelClassName="w-full"
                valueClassName="w-full"
                text={String(
                  portServiceDetails.receivingVesselImo ??
                    portServiceDetails.receivingVesselImoNumber ??
                    "-",
                )}
              />
              <PaperItem
                testId={`${TEST_ID_BASE}-receiving-vessel-name`}
                translate={t(`${langBase}.receiving-vessel-name`)}
                textStyles="font-medium"
                tranStyle={transStyleClass}
                wrapperStyle={wrapperStyleClass}
                labelClassName="w-full"
                valueClassName="w-full"
                text={portServiceDetails.receivingVesselName || "-"}
              />
              <PaperItem
                testId={`${TEST_ID_BASE}-remarks`}
                translate={t(`${langBase}.remarks`)}
                textStyles="font-medium"
                tranStyle={transStyleClass}
                wrapperStyle={wrapperLastStyleClass}
                labelClassName="w-full"
                valueClassName="w-full"
                text={portServiceDetails.remarks || "-"}
              />
            </div>
          </div>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <p className="font-bold">{t(`${langBase}.service-details`)}</p>
          <div className="grid grid-cols-2">
            <PaperItem
              testId={`${TEST_ID_BASE}-service-category`}
              translate={t(`${langBase}.service-category`)}
              textStyles="font-medium"
              tranStyle={transStyleClass}
              wrapperStyle={shipToShipTopRowWrapperStyle}
              text={serviceCategoryLabel || "-"}
            />

            {!isBunkerBargeService && !isShipToShip && (
              <PaperItem
                testId={`${TEST_ID_BASE}-service-type`}
                translate={t(`${langBase}.service-type`)}
                textStyles="font-medium"
                tranStyle={transStyleClass}
                wrapperStyle={lastWrapperStyle}
                text={portServiceDetails.serviceType || "-"}
              />
            )}
            {!isBunkerBargeService && portServiceDetails.garbageCategory && (
              <PaperItem
                testId={`${TEST_ID_BASE}-garbage-category`}
                translate={t(`${langBase}.garbage-category`)}
                textStyles="font-medium"
                tranStyle={transStyleClass}
                wrapperStyle={wrapperStyleClass}
                text={portServiceDetails.garbageCategory || "-"}
              />
            )}
            {isShipToShip && (
              <>
                <div className="col-start-2">
                  <ShipToShipFields langBase={langBase} portServiceDetails={portServiceDetails} />
                </div>
                {!isBunkerBargeService && (
                  <div>
                    <PaperItem
                      testId={`${TEST_ID_BASE}-service-type`}
                      translate={t(`${langBase}.service-type`)}
                      textStyles="font-medium"
                      tranStyle={transStyleClass}
                      wrapperStyle={wrapperStyleClass}
                      text={portServiceDetails.serviceType || "-"}
                    />
                    <PaperItem
                      testId={`${TEST_ID_BASE}-service-subtype`}
                      translate={t(`${langBase}.service-subtype`)}
                      textStyles="font-medium"
                      tranStyle={transStyleClass}
                      wrapperStyle={wrapperLastStyleClass}
                      text={portServiceDetails.serviceSubType || "-"}
                    />
                  </div>
                )}
                <div className="col-start-2">
                  <PaperItem
                    testId={`${TEST_ID_BASE}-other-vessel-imo-number`}
                    translate={t(`${langBase}.other-vessel-imo-number`)}
                    textStyles="font-medium"
                    tranStyle={transStyleClass}
                    wrapperStyle={wrapperStyleClass}
                    text={
                      canNavigateToOtherVessel ? (
                        <button
                          type="button"
                          data-testid={`${TEST_ID_BASE}-other-vessel-imo-number-link`}
                          className="cursor-pointer text-[#0057B8] underline underline-offset-2"
                          onClick={handleOtherVesselClick}
                        >
                          {otherVesselImo}
                        </button>
                      ) : (
                        otherVesselImo
                      )
                    }
                  />
                </div>
              </>
            )}

            {!isBunkerBargeService && !isShipToShip && portServiceDetails.serviceSubType && (
              <PaperItem
                testId={`${TEST_ID_BASE}-service-subtype`}
                translate={t(`${langBase}.service-subtype`)}
                textStyles="font-medium"
                tranStyle={transStyleClass}
                wrapperStyle={wrapperLastStyleClass}
                text={portServiceDetails.serviceSubType || "-"}
              />
            )}
            {isBunkeringService && (
              <BunkeringFields langBase={langBase} portServiceDetails={portServiceDetails} />
            )}
            {isDebunkeringService && (
              <DebunkeringFields langBase={langBase} portServiceDetails={portServiceDetails} />
            )}
            <PaperItem
              testId={`${TEST_ID_BASE}-remarks`}
              translate={t(`${langBase}.remarks`)}
              textStyles="font-medium"
              tranStyle={transStyleClass}
              wrapperStyle={wrapperStyleClass}
              text={portServiceDetails.remarks || "-"}
            />
            {!isSludgeService && !isSlopsService && !isBunkerBargeService && !isShipToShip && (
              <div>
                <PaperItem
                  testId={`${TEST_ID_BASE}-location`}
                  translate={t(`${langBase}.location`)}
                  textStyles="font-medium"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={portServiceDetails.location || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-quantity`}
                  translate={t(`${langBase}.quantity`)}
                  textStyles="font-medium"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={portServiceDetails.quantity || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-unit-of-measurement`}
                  translate={t(`${langBase}.unit-of-measurement`)}
                  textStyles="font-medium"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={portServiceDetails.unitOfMeasurement || "-"}
                />
                <PaperItem
                  testId={`${TEST_ID_BASE}-number-of-skips`}
                  translate={t(`${langBase}.number-of-skips`)}
                  textStyles="font-medium"
                  tranStyle={transStyleClass}
                  wrapperStyle={wrapperStyleClass}
                  text={portServiceDetails.numberOfSkips || "-"}
                />
              </div>
            )}
          </div>
        </Paper>
      )}
      {isSludgeService && (
        <SludgeFields langBase={langBase} portServiceDetails={portServiceDetails} />
      )}
      {isSlopsService && (
        <WasteDisposalSlopsFields langBase={langBase} portServiceDetails={portServiceDetails} />
      )}
      <Paper variant="outlined">
        <p className="font-bold">{t(`${langBase}.times-and-estimates`)}</p>
        <div className="grid grid-cols-2">
          <PaperItem
            testId={`${TEST_ID_BASE}-rts`}
            translate={t(`${langBase}.rts`)}
            textStyles="font-medium"
            tranStyle={transStyleClass}
            wrapperStyle={wrapperStyleClass}
            text={
              portServiceDetails.rts ? format(portServiceDetails.rts, "dd/MM/yyyy' 'HH:mm") : "-"
            }
          />
          <PaperItem
            testId={`${TEST_ID_BASE}-etc`}
            translate={t(`${langBase}.etc`)}
            textStyles="font-medium"
            tranStyle={transStyleClass}
            wrapperStyle={wrapperStyleClass}
            text={
              portServiceDetails.etc ? format(portServiceDetails.etc, "dd/MM/yyyy' 'HH:mm") : "-"
            }
          />
          <PaperItem
            testId={`${TEST_ID_BASE}-ets`}
            translate={t(`${langBase}.ets`)}
            textStyles="font-medium"
            tranStyle={transStyleClass}
            wrapperStyle={wrapperStyleClass}
            text={
              portServiceDetails.ets ? format(portServiceDetails.ets, "dd/MM/yyyy' 'HH:mm") : "-"
            }
          />
          <PaperItem
            testId={`${TEST_ID_BASE}-atc`}
            translate={t(`${langBase}.atc`)}
            textStyles="font-medium"
            tranStyle={transStyleClass}
            wrapperStyle={wrapperStyleClass}
            text={
              portServiceDetails.atc ? format(portServiceDetails.atc, "dd/MM/yyyy' 'HH:mm") : "-"
            }
          />
          <PaperItem
            testId={`${TEST_ID_BASE}-ats`}
            translate={t(`${langBase}.ats`)}
            textStyles="font-medium"
            tranStyle={transStyleClass}
            wrapperStyle={wrapperLastStyleClass}
            text={
              portServiceDetails.ats ? format(portServiceDetails.ats, "dd/MM/yyyy' 'HH:mm") : "-"
            }
          />
        </div>
      </Paper>
      {isShipChandlingLifeboats && (
        <div>
          <p className="pb-4 text-[20px] font-normal leading-6 text-[#646A77]">
            {t(`${langBase}.additional-resources`)}
          </p>
          <Paper variant="outlined">
            {shipChandlingResources.length ? (
              shipChandlingResources.map((resource, index) => (
                <div key={`${resource.name}-${index}`} className={index ? "pt-6" : "pt-2"}>
                  <p className="pb-4 font-bold text-[#333333]">
                    {t(`${langBase}.resource`, { index: index + 1 })}
                  </p>
                  <div className="grid grid-cols-2 gap-x-8">
                    <PaperItem
                      testId={`${TEST_ID_BASE}-resource-name-${index}`}
                      translate={t(`${langBase}.resources-equipment`)}
                      textStyles="font-medium"
                      tranStyle={transStyleClass}
                      wrapperStyle={wrapperStyleClass}
                      labelClassName="w-full"
                      valueClassName="w-full"
                      text={resource.name}
                    />
                    <PaperItem
                      testId={`${TEST_ID_BASE}-resource-count-${index}`}
                      translate={t(`${langBase}.number-of-resources`)}
                      textStyles="font-medium"
                      tranStyle={transStyleClass}
                      wrapperStyle={wrapperStyleClass}
                      labelClassName="w-full"
                      valueClassName="w-full"
                      text={resource.count}
                    />
                  </div>
                  {resource.start && resource.end && (
                    <div className="grid grid-cols-2 gap-x-8">
                      <PaperItem
                        testId={`${TEST_ID_BASE}-resource-name-${index}`}
                        translate={t(`${langBase}.start-time`)}
                        textStyles="font-medium"
                        tranStyle={transStyleClass}
                        wrapperStyle={wrapperStyleClass}
                        labelClassName="w-full"
                        valueClassName="w-full"
                        text={resource.start}
                      />
                      <PaperItem
                        testId={`${TEST_ID_BASE}-resource-count-${index}`}
                        translate={t(`${langBase}.end-time`)}
                        textStyles="font-medium"
                        tranStyle={transStyleClass}
                        wrapperStyle={wrapperStyleClass}
                        labelClassName="w-full"
                        valueClassName="w-full"
                        text={resource.end}
                      />
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="pt-4 font-medium">-</div>
            )}
          </Paper>
        </div>
      )}
      {portServiceDetails.description ? (
        <Paper variant="outlined">
          <div data-testid={`${TEST_ID_BASE}-service-description`}>
            <p className="font-bold">{t(`${langBase}.service-description`)}</p>
            <div className="pt-4 font-normal whitespace-pre-wrap wrap-break-word">
              {portServiceDetails.description || "-"}
            </div>
          </div>
        </Paper>
      ) : null}
    </>
  );
};

export default ServiceDetails;
